import React, { useContext, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useLocation } from '@reach/router';
import * as Sentry from '@sentry/react';
import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { useAccountStore } from '@components/dashboard/context/UserDashboardContext';
import { H1 } from '@components/dashboard/Typography';
import useCustomer from '@components/dashboard/utils/useCustomer';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import useDesktop from '@components/shared/useDesktop';
import { VESICA_SVG } from '@constants';
import { pageIsLoginPage } from '@utils/helpers';
import useNewsletter from '@utils/useNewsletter';
import Marketing from './CreateAccountMarketing';
import Terms from './CreateAccountTerms';

interface Props {
  closeable?: boolean;
}

export default function AcceptTerms({ closeable }: Props) {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { firebaseAuth } = state;
  const [tandcs, setTandcs] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const customer = useCustomer(state);
  const { addToNewsletter, removeFromNewsletter } = useNewsletter('accept-terms');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStyles, setModalStyles] = useState({});
  const isDesktop = useDesktop(640);
  const userInfo = useAccountStore((accountState) => accountState.userInfo);
  const location = useLocation();

  useEffect(() => {
    setModalStyles({
      content: {
        top: '20%',
        left: isDesktop ? '50%' : '0',
        right: isDesktop ? 'auto' : '0',
        bottom: isDesktop ? 'auto' : '0',
        marginRight: isDesktop ? '-50%' : '0',
        transform: isDesktop ? 'translate(-50%, -20%)' : 'none',
        width: isDesktop ? '600px' : '100%',
        height: 'auto',
        padding: '0',
        border: '0',
        borderRadius: isDesktop ? '0' : '20px 20px 0 0',
        background: Colors.cream,
        shadowColor: 'rgba(0, 28, 59, 0.3)',
        shadowOpacity: 0.6
      },
      overlay: {
        background: `${Colors.navyBlue}25`
      }
    });
  }, [isDesktop]);

  useEffect(() => {
    const isModalOpen = !!userInfo.accountId && !userInfo?.termsAcceptedDate && !pageIsLoginPage(location.pathname);

    setModalOpen(isModalOpen);
  }, [userInfo.termsAcceptedDate, userInfo.accountId, location]);

  const handleToggleTCs = () => {
    setTandcs(!tandcs);
  };

  const handleToggleMarketing = () => {
    setMarketing(!marketing);
  };

  const handleSubmit = async () => {
    if (!tandcs) {
      return;
    }

    setLoading(true);

    try {
      if (marketing) {
        await addToNewsletter(firebaseAuth);
      } else {
        await removeFromNewsletter(firebaseAuth);
      }

      await customer.update({ termsAcceptedDate: Timestamp.now() });
    } catch (err) {
      setLoading(false);
      console.error('Error: ', JSON.stringify(err));
      Sentry.captureMessage('Failed to handle accept terms');
    }
  };

  return (
    <Wrapper>
      <ReactModal isOpen={modalOpen} style={modalStyles} onRequestClose={() => (closeable ? setModalOpen(false) : null)}>
        <Container>
          <img src={VESICA_SVG} alt="vesica" />
          <H1Styled>{t('components.welcome-to-daye')}</H1Styled>
          <StyledP>{t('components.please-confirm-tac')}</StyledP>
          <CheckboxesWrap>
            <Terms onClick={handleToggleTCs} checked={tandcs} />
            <Marketing onClick={handleToggleMarketing} checked={marketing} />
          </CheckboxesWrap>
          <ButtonStyled disabled={!tandcs} onClick={handleSubmit} loading={loading}>
            {t('components.continue')}
          </ButtonStyled>
        </Container>
      </ReactModal>
    </Wrapper>
  );
}

const Wrapper = styled.div``;

const Container = styled.div`
  user-select: none;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 0 60px 0;

  > img {
    width: 102px;
    height: auto;
    margin-bottom: 40px;
  }
`;

const H1Styled = styled(H1)`
  margin-bottom: 15px;

  @media (max-width: 767px) {
    margin-bottom: 10px;
  }
`;

const StyledP = styled.p`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: ${Colors.navyBlue};
  margin-bottom: 40px;

  @media (max-width: 767px) {
    margin-bottom: 30px;
    font-size: 14px;
  }

  @media (max-width: 640px) {
    padding: 0 20px;
  }
`;

const CheckboxesWrap = styled.div`
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 20px;

  @media (max-width: 767px) {
    margin-bottom: 30px;
  }

  @media (max-width: 640px) {
    padding: 0 20px 0 30px;
  }
`;

const ButtonStyled = styled(Button)`
  background: ${Colors.pink};

  @media (width: 768px) {
    width: 100%;
  }
`;
